import { create } from "zustand"

import { getTournamentDetails } from "@/shared/api/endpoints"
import {
  parseRealtimeTournamentState,
  parseTournamentDetails,
  parseTournamentSummary,
} from "@/shared/lib/parseTournamentState"
import type {
  TournamentDetails,
  TournamentSummary,
} from "@/shared/api/types/tournaments"

/**
 * Single-tournament store (the contract supports multiple via
 * `active_tournaments.items[]`, but for now we always take `items[0]`).
 *
 * Two slots:
 *  - `summary` — basic info from bootstrap (or refreshed from realtime). Drives
 *    the PvP banner, welcome modal, Rewards card.
 *  - `details` — full data (reward_promises, top-10 with rank + promised_reward,
 *    myStanding) fetched from `GET /v1/games/tournaments/{id}` when the user
 *    opens the leaderboard page.
 *
 * Realtime updates the summary (and the cached details if it matches the same
 * id); `my_standing` isn't in the realtime payload, so it's only refreshed by
 * the next `fetchDetails` call.
 */
interface TournamentStoreState {
  summary: TournamentSummary | null
  details: TournamentDetails | null
  detailsLoading: boolean
  detailsError: string | null

  /** Take items[0] from `active_tournaments.items[]` (bootstrap). */
  ingestBootstrapItems: (items: unknown[]) => void
  /** Update from a realtime `TournamentUpdated` payload (no my_standing). */
  ingestRealtimeUpdate: (rawTournamentEnvelope: unknown) => void
  /** Fetch fresh details from the endpoint (called on leaderboard page mount). */
  fetchDetails: (tournamentId: string) => Promise<void>
  reset: () => void
}

export const useTournamentStore = create<TournamentStoreState>((set, get) => ({
  summary: null,
  details: null,
  detailsLoading: false,
  detailsError: null,

  ingestBootstrapItems: (items) => {
    const first = items[0]
    if (first == null) {
      set({ summary: null })
      return
    }
    const summary = parseTournamentSummary(first)
    if (!summary) {
      if (import.meta.env.DEV) {
        console.warn("[tournamentStore] bootstrap items[0] ignored — malformed")
      }
      return
    }
    set({ summary })
    // If we hold details for a different tournament, clear them.
    const prevDetails = get().details
    if (prevDetails && prevDetails.tournament.id !== summary.id) {
      set({ details: null, detailsError: null })
    }
  },

  ingestRealtimeUpdate: (raw) => {
    const parsed = parseRealtimeTournamentState(raw)
    if (!parsed.data) {
      if (import.meta.env.DEV) {
        console.warn("[tournamentStore] realtime payload ignored:", parsed.error)
      }
      return
    }
    const { tournament, leaderboard } = parsed.data
    const prev = get()
    // Update summary (always, when ids match or when we have no summary yet).
    if (!prev.summary || prev.summary.id === tournament.id) {
      // strip reward_promises down to a summary projection
      const {
        rewardPromises: _unused,
        ...summary
      } = tournament
      void _unused
      set({ summary })
    }
    // Update cached details for the same id (leaves myStanding sticky — it's
    // not in the realtime payload; next `fetchDetails` refreshes it).
    if (prev.details && prev.details.tournament.id === tournament.id) {
      set({
        details: {
          ...prev.details,
          tournament,
          leaderboard,
          totalCount: Math.max(leaderboard.length, prev.details.totalCount),
        },
      })
    }
  },

  fetchDetails: async (tournamentId) => {
    set({ detailsLoading: true, detailsError: null })
    try {
      const raw = await getTournamentDetails(tournamentId, {
        limit: 50,
        offset: 0,
      })
      const parsed = parseTournamentDetails(raw)
      if (!parsed.data) {
        set({ detailsLoading: false, detailsError: parsed.error })
        return
      }
      // Keep `summary` in sync with the freshly fetched tournament.
      const {
        rewardPromises: _unused,
        ...summary
      } = parsed.data.tournament
      void _unused
      set({
        details: parsed.data,
        summary,
        detailsLoading: false,
        detailsError: null,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      set({ detailsLoading: false, detailsError: message })
    }
  },

  reset: () =>
    set({
      summary: null,
      details: null,
      detailsLoading: false,
      detailsError: null,
    }),
}))
